import ErrorState from '@/components/ErrorState';
import ProductAccordions, {
  ProductAccordion,
} from '@/components/ProductDetails/ProductAccordions';
import { colors } from '@/utils/theme';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { BLOCKS, MARKS, INLINES, Document } from '@contentful/rich-text-types';
import {
  Box,
  Text,
  Button,
  Tag,
  Grid,
  Flex,
  Heading,
  Image,
} from '@everlywell/ui-kit';
import { MediaComponent } from 'components/Video';
import { Link } from 'gatsby';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import React, { useState, useMemo } from 'react';

export type ProductData = {
  product: {
    name: string;
    title: string;
    shortDescription: string;
    buttons: {
      primary: {
        text: string;
        link: string;
      };
      secondary: {
        text: string;
        link?: string;
      };
    };
    media: {
      video: {
        url: string;
        videoThumb: string;
        subtitles?: string;
        title: string;
        videoText?: string;
        disclaimer?: string;
      };
      image?: IGatsbyImageData;
    };
  };
  tabs: Array<{
    title: string;
    description: Document; // Using proper Contentful Rich Text Document type
    measures?: string[];
    sampleTypes?: Array<{
      text: string;
      icon: string;
    }>;
    productAccordions?: ProductAccordion[];
  }>;
};

type Props = {
  data?: ProductData;
};

// Memoize render options outside component to prevent recreation
const richTextRenderOptions = {
  renderMark: {
    [MARKS.BOLD]: (text: string) =>
      `<strong style="font-weight: 600;">${text}</strong>`,
    [MARKS.ITALIC]: (text: string) => `<em>${text}</em>`,
    [MARKS.UNDERLINE]: (text: string) => `<u>${text}</u>`,
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: any, next: any) =>
      `<p>${next(node.content)}</p>`,
    [BLOCKS.HEADING_1]: (node: any, next: any) =>
      `<h1 style="font-size: 2em; margin-bottom: 1rem;">${next(
        node.content,
      )}</h1>`,
    [BLOCKS.HEADING_2]: (node: any, next: any) =>
      `<h2 style="font-size: 1.5em; margin-bottom: 1rem;">${next(
        node.content,
      )}</h2>`,
    [BLOCKS.HEADING_3]: (node: any, next: any) =>
      `<h3 style="font-size: 1.17em; margin-bottom: 1rem;">${next(
        node.content,
      )}</h3>`,
    [BLOCKS.UL_LIST]: (node: any, next: any) =>
      `<ul style="list-style-type: disc; padding-left: 1.5rem; margin-bottom: 1rem;">${next(
        node.content,
      )}</ul>`,
    [BLOCKS.OL_LIST]: (node: any, next: any) =>
      `<ol style="list-style-type: decimal; padding-left: 1.5rem; margin-bottom: 1rem;">${next(
        node.content,
      )}</ol>`,
    [BLOCKS.LIST_ITEM]: (node: any, next: any) =>
      `<li style="margin-bottom: 0.5rem;">${next(node.content)}</li>`,
    [INLINES.HYPERLINK]: (node: any, next: any) =>
      `<a href="${node.data.uri}" style="color: ${
        colors.primary.main
      }; text-decoration: underline;">${next(node.content)}</a>`,
  },
};

/**
 * ProductDetails component displays detailed information about a product
 */
export default function ProductDetails({ data }: Props) {
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // Rich text rendering options and HTML conversion
  const tabDescriptionHtml = useMemo(() => {
    const currentTab = data?.tabs?.[activeTabIndex];
    if (!currentTab?.description) return '';

    try {
      return documentToHtmlString(
        currentTab.description,
        richTextRenderOptions,
      );
    } catch {
      return ''; // If parsing fails, return empty string
    }
  }, [data?.tabs, activeTabIndex]);

  if (!data) {
    return <ErrorState message="Product details not found" />;
  }

  const activeTab = data.tabs?.[activeTabIndex];

  return (
    <Box as="section" pt={['5rem', '6rem']} mb="5rem" id="product-details">
      <Box width="100%" maxWidth="1440px" mx="auto" px="1rem">
        <Flex gap={[8, 8, 12, 20]} flexDirection={['column', 'column', 'row']}>
          <Box
            width={['100%', '100%', '50%', '60%']}
            display="flex"
            flexDirection="column"
            gap={4}
            order={['1', '1', '0']}
          >
            {data.product.media.video?.videoThumb &&
            data.product.media.video?.videoThumb !== '' ? (
              <MediaComponent
                url={data.product.media.video.url}
                videoThumb={data.product.media.video.videoThumb}
                subtitles={data.product.media.video.subtitles}
                title={data.product.media.video.title}
                videoText={data.product.media.video.videoText}
                disclaimer={data.product.media.video.disclaimer}
              />
            ) : data.product.media.image ? (
              <GatsbyImage
                image={data.product.media.image}
                alt={data.product.name}
                loading="lazy"
              />
            ) : (
              <Box
                borderRadius="lg"
                bg={colors.background.light}
                height="300px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text color={colors.text.secondary}>
                  No media available for this product
                </Text>
              </Box>
            )}

            {/* Product Tabs */}
            <Box width="100%" position="relative">
              <Flex
                width="100%"
                gap={0}
                overflowX="auto"
                css={{
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                  '-ms-overflow-style': 'none',
                  'scrollbar-width': 'none',
                }}
              >
                {data?.tabs?.length > 1 &&
                  data.tabs.map((tab, index) => (
                    <Button
                      key={tab.title}
                      variant="ghost"
                      bg={
                        activeTabIndex === index
                          ? colors.primary.dark
                          : colors.background.main
                      }
                      color={
                        activeTabIndex === index
                          ? 'white'
                          : colors.text.secondary
                      }
                      borderTop={`1px solid ${colors.background.border}`}
                      borderBottom={`1px solid ${colors.background.border}`}
                      borderLeft={`1px solid ${colors.background.border}`}
                      borderRight={
                        index === data.tabs.length - 1
                          ? `1px solid ${colors.background.border}`
                          : 'none'
                      }
                      borderTopLeftRadius={index === 0 ? '0.5rem' : '0'}
                      borderTopRightRadius={
                        index === data.tabs.length - 1 ? '0.5rem' : '0'
                      }
                      borderBottomLeftRadius={index === 0 ? '0.5rem' : '0'}
                      borderBottomRightRadius={
                        index === data.tabs.length - 1 ? '0.5rem' : '0'
                      }
                      py={3}
                      px={4}
                      onClick={() => setActiveTabIndex(index)}
                      whiteSpace="nowrap"
                      fontWeight="500"
                      fontSize={['12px', '12px', '16px']}
                      flex={1}
                      _hover={{
                        bg:
                          activeTabIndex === index
                            ? colors.primary.dark
                            : colors.background.offWhite,
                      }}
                      marginRight="0"
                    >
                      {tab.title}
                    </Button>
                  ))}
              </Flex>
            </Box>

            {/* Tab Content */}
            <Box width="100%" id="product-tab-content">
              {activeTab && (
                <Box
                  p={6}
                  borderRadius="lg"
                  bg={colors.background.light}
                  width="100%"
                >
                  <Box
                    fontSize={['15px', '18px']}
                    marginBottom={6}
                    color="#545454"
                    className="product-description"
                    dangerouslySetInnerHTML={{
                      __html: tabDescriptionHtml,
                    }}
                    sx={{
                      '& a': {
                        color: colors.primary.main,
                        textDecoration: 'underline',
                      },
                      '& p:not(:last-child)': {
                        marginBottom: '1rem',
                      },
                      '& ul, & ol': {
                        paddingLeft: '1.5rem',
                        marginBottom: '1rem',
                      },
                      '& li': {
                        marginBottom: '0.5rem',
                      },
                      '& h1, & h2, & h3, & h4, & h5, & h6': {
                        marginBottom: '1rem',
                        fontWeight: '500',
                      },
                    }}
                  />

                  {activeTab.measures && activeTab.measures.length > 0 && (
                    <Box>
                      <Flex flexWrap="wrap" gap={2} mb={6} mt={10}>
                        {activeTab.measures.map((measure) => (
                          <Tag
                            key={measure}
                            size="md"
                            borderRadius="full"
                            variant="solid"
                            bg="#E8E8E2"
                            color="#545454"
                            px={3}
                            py={1}
                          >
                            {measure}
                          </Tag>
                        ))}
                      </Flex>
                    </Box>
                  )}

                  {activeTab.sampleTypes && (
                    <Grid gridTemplateColumns={['1fr', '1fr 1fr']} gap={4}>
                      {activeTab.sampleTypes.map(({ text, icon }) => (
                        <Flex
                          key={text}
                          alignItems="center"
                          justifyContent="space-between"
                          gap={2}
                        >
                          <Image src={icon} alt={text} width={32} height={32} />
                          <Text size="md" color={colors.text.secondary}>
                            {text}
                          </Text>
                        </Flex>
                      ))}
                    </Grid>
                  )}
                </Box>
              )}
            </Box>

            {activeTab?.productAccordions && (
              <ProductAccordions accordions={activeTab.productAccordions} />
            )}
          </Box>

          <Box width={['100%', '100%', '50%', '40%']} order={['0', '0', '1']}>
            <Box
              position="sticky"
              top="100px"
              zIndex={100}
              style={{
                transform: 'translateZ(0)',
                willChange: 'transform',
                WebkitBackfaceVisibility: 'hidden',
                WebkitPerspective: 1000,
              }}
            >
              <Box
                width="100%"
                margin="0 auto"
                display="flex"
                flexDirection="column"
                alignItems={['center', 'flex-start']}
                gap="1rem"
                px={['2', '0']}
              >
                <Box
                  width="100%"
                  display="flex"
                  flexDirection="column"
                  alignItems={['center', 'flex-start']}
                  gap="0.5rem"
                  textAlign={['center', 'left']}
                >
                  <Text
                    color={colors.primary.main}
                    fontSize={['15px', '16px']}
                    textAlign={['center', 'left']}
                  >
                    {data.product.name}
                  </Text>
                  <Heading
                    maxWidth={['100%', '100%', '100%', '100%']}
                    fontFamily="heading"
                    fontWeight="semibold"
                    textAlign={['center', 'left']}
                    marginRight={['0', 'auto']}
                    fontSize={['30px', '36px']}
                    lineHeight={['1.25', '1.2']}
                  >
                    {data.product.title}
                  </Heading>
                </Box>
                {data.product.shortDescription && (
                  <Text
                    maxWidth={['100%', '100%', '100%', '100%']}
                    textAlign={['center', 'left']}
                    color={colors.text.primary}
                    lineHeight="1.5rem"
                    fontSize={['15px', '16px']}
                    fontWeight="400"
                  >
                    {data.product.shortDescription}
                  </Text>
                )}
              </Box>

              <Flex
                mt={8}
                gap={[4, 4, 0]}
                width="100%"
                flexDirection={['column', 'column', 'row']}
                alignItems={['center', 'center', 'flex-start']}
              >
                <Button
                  as={Link}
                  to={data.product.buttons.primary.link}
                  width={['auto', '180px']}
                  height="52px"
                  px="1.5rem"
                  mr={[0, 0, '1.5rem']}
                  bg={colors.secondary.main}
                  color="white"
                  borderRadius="1.75rem"
                  fontSize={['15px', '16px']}
                  fontWeight="500"
                  _hover={{
                    backgroundColor: colors.secondary.main,
                  }}
                >
                  {data.product.buttons.primary.text}
                </Button>

                {data.product.buttons.secondary.text &&
                  (data.product.buttons.secondary.link ? (
                    <Button
                      as={Link}
                      to={data.product.buttons.secondary.link}
                      width={['auto', '180px']}
                      height="52px"
                      px="1.5rem"
                      variant="outline"
                      borderColor={colors.text.secondary}
                      color={colors.text.secondary}
                      borderRadius="1.75rem"
                      fontSize={['15px', '16px']}
                      fontWeight="500"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {data.product.buttons.secondary.text}
                      <Box ml="0.5rem" display="inline-block">
                        <svg
                          width="16"
                          height="18"
                          viewBox="0 0 16 18"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M15.2803 11.0306L8.53031 17.7806C8.46066 17.8504 8.37794 17.9057 8.28689 17.9434C8.19584 17.9812 8.09825 18.0006 7.99968 18.0006C7.90112 18.0006 7.80353 17.9812 7.71248 17.9434C7.62143 17.9057 7.53871 17.8504 7.46906 17.7806L0.71906 11.0306C0.578329 10.8899 0.499268 10.699 0.499268 10.5C0.499268 10.301 0.578329 10.1101 0.71906 9.96937C0.85979 9.82864 1.05066 9.74958 1.24968 9.74958C1.44871 9.74958 1.63958 9.82864 1.78031 9.96937L7.24968 15.4397V0.75C7.24968 0.551088 7.3287 0.360322 7.46935 0.21967C7.61001 0.0790178 7.80077 0 7.99968 0C8.1986 0 8.38936 0.0790178 8.53002 0.21967C8.67067 0.360322 8.74968 0.551088 8.74968 0.75V15.4397L14.2191 9.96937C14.3598 9.82864 14.5507 9.74958 14.7497 9.74958C14.9487 9.74958 15.1396 9.82864 15.2803 9.96937C15.421 10.1101 15.5001 10.301 15.5001 10.5C15.5001 10.699 15.421 10.8899 15.2803 11.0306Z"
                            fill={colors.text.secondary}
                          />
                        </svg>
                      </Box>
                    </Button>
                  ) : (
                    <Button
                      width={['auto', '180px']}
                      height="52px"
                      px="1.5rem"
                      color="#0F0F0F"
                      borderRadius="1.75rem"
                      fontSize={['15px', '16px']}
                      fontWeight="500"
                      onClick={() => {
                        const element = document.getElementById(
                          'end-product-details',
                        );
                        if (element) {
                          element.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                          });
                        }
                      }}
                      sx={{
                        backgroundColor: '#F2F2ED !important',
                      }}
                    >
                      {data.product.buttons.secondary.text}
                      <Box ml="0.5rem" display="inline-block">
                        <svg
                          width="16"
                          height="18"
                          viewBox="0 0 16 18"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M15.2803 11.0306L8.53031 17.7806C8.46066 17.8504 8.37794 17.9057 8.28689 17.9434C8.19584 17.9812 8.09825 18.0006 7.99968 18.0006C7.90112 18.0006 7.80353 17.9812 7.71248 17.9434C7.62143 17.9057 7.53871 17.8504 7.46906 17.7806L0.71906 11.0306C0.578329 10.8899 0.499268 10.699 0.499268 10.5C0.499268 10.301 0.578329 10.1101 0.71906 9.96937C0.85979 9.82864 1.05066 9.74958 1.24968 9.74958C1.44871 9.74958 1.63958 9.82864 1.78031 9.96937L7.24968 15.4397V0.75C7.24968 0.551088 7.3287 0.360322 7.46935 0.21967C7.61001 0.0790178 7.80077 0 7.99968 0C8.1986 0 8.38936 0.0790178 8.53002 0.21967C8.67067 0.360322 8.74968 0.551088 8.74968 0.75V15.4397L14.2191 9.96937C14.3598 9.82864 14.5507 9.74958 14.7497 9.74958C14.9487 9.74958 15.1396 9.82864 15.2803 9.96937C15.421 10.1101 15.5001 10.301 15.5001 10.5C15.5001 10.699 15.421 10.8899 15.2803 11.0306Z"
                            fill={colors.text.secondary}
                          />
                        </svg>
                      </Box>
                    </Button>
                  ))}
              </Flex>
            </Box>
          </Box>
        </Flex>

        <Box id="end-product-details" />
      </Box>
    </Box>
  );
}
